import type { Language } from '../types';

/** Commercial / product buying copy (EN · AR · CKB). Merged into translations. */
export const commercialCopy: Record<Language, Record<string, string>> = {
  en: {
    'product.display.glasspos': 'GlassPOS',
    'product.display.iqStore': 'IQ Store',
    'product.display.raqib': 'Raqb',
    'product.display.befx': 'BeFX',
    'product.display.ledger': 'Raqb',
    'product.display.bepharma': 'BePharma',

    'commercial.glasspos.tagline': 'Sell faster, monitor stock, and understand your daily sales.',
    'commercial.glasspos.description':
      'GlassPOS is a fast, practical Point of Sale system for businesses with frequent daily counter sales. It focuses on quick checkout, barcode sales, products, stock visibility, customers, and daily sales reporting — for shops where speed of selling is the priority.',
    'commercial.glasspos.problem1': 'Speed up counter checkout for busy daily selling.',
    'commercial.glasspos.problem2': 'Sell with barcodes while keeping stock and customers visible.',
    'commercial.glasspos.problem3': 'Know your daily sales without slowing the counter.',
    'commercial.glasspos.feature1': 'Fast daily checkout and barcode sales',
    'commercial.glasspos.feature2': 'Products, stock visibility, and customers',
    'commercial.glasspos.feature3': 'Clear daily sales reporting',
    'commercial.glasspos.feature4': 'Built for fast-moving retail counters',
    'commercial.glasspos.fit':
      'If your business needs deeper catalog and inventory organization rather than primarily fast checkout, {iq-store} may be a better fit.',

    'commercial.iqStore.tagline': 'Organize your products, inventory, and store from one place.',
    'commercial.iqStore.description':
      'IQ Store is for businesses where managing products, inventory, catalog structure, and store operations matters more than simply fast checkout. It helps you organize complex catalogs and inventory-heavy retail from one place — not another version of the same POS.',
    'commercial.iqStore.problem1': 'Structure large or diverse product catalogs without losing control.',
    'commercial.iqStore.problem2': 'Keep inventory and store operations organized as the catalog grows.',
    'commercial.iqStore.problem3': 'Run product-heavy retail without treating every sale as a simple barcode checkout.',
    'commercial.iqStore.feature1': 'Product and catalog organization',
    'commercial.iqStore.feature2': 'Inventory and stock visibility for complex stores',
    'commercial.iqStore.feature3': 'Store operations beyond basic checkout',
    'commercial.iqStore.feature4': 'Built for product- and inventory-heavy retail',
    'commercial.iqStore.fit':
      'If your main priority is high-speed daily counter checkout, {glasspos} may be the simpler fit.',

    'commercial.raqib.tagline': 'All your financial obligations in one place — money, receivables, and spending clarity.',
    'commercial.raqib.description':
      'Raqib (راقب) is BeCoder’s financial monitoring application for business finances. It helps owners watch money movement, income, expenses, and receivables — so you can see what the business is owed and what it owes, and keep a clearer picture of where money goes. It is not a POS or cashier system, not an inventory/catalog system, not a currency-exchange desk, and not a heavy certified accounting or ERP suite. Raqib is preparing for customer launch on BeCoder.',
    'commercial.raqib.whoLede':
      'For business owners and teams who need clearer financial monitoring — not a retail checkout counter.',
    'commercial.raqib.benefitsTitle': 'Key benefits',
    'commercial.raqib.explainHeading': 'Understand Raqib',
    'commercial.raqib.explain.what.title': 'What is Raqib?',
    'commercial.raqib.explain.what.body':
      'Raqib is simple financial monitoring and business money oversight. Watch money in and money out, keep income and expenses visible, and stay aware of receivables and what the business owes — without the complexity of certified accounting software.',
    'commercial.raqib.explain.debts.title': 'Receivables & money owed',
    'commercial.raqib.explain.debts.body':
      'Part of Raqib’s purpose is clearer visibility of money owed to the business (receivables) and money the business owes. The goal is a practical overview of what you are owed and what you owe — not a full accounting ledger or certified statements.',
    'commercial.raqib.explain.business.title': 'Business income & expenses',
    'commercial.raqib.explain.business.body':
      'Raqib is positioned to help owners understand business money movement: income received, operating expenses, and a simpler view of financial position. Typical audiences include companies, contractors, offices, traders, distributors, workshops, SMEs, and independent owners.',
    'commercial.raqib.explain.not.title': 'What Raqib is not',
    'commercial.raqib.explain.not.body':
      'Raqib is not a cashier POS, not product/inventory retail software, not a currency-exchange operations system, and not certified accounting, tax, audit, or ERP software.',
    'commercial.raqib.explain.diff.title': 'How it differs from other BeCoder products',
    'commercial.raqib.explain.diff.body':
      'GlassPOS is for fast daily retail checkout. IQ Store is for catalog- and inventory-heavy retail. BeFX is for currency-exchange offices. Raqib is for monitoring business money, receivables, income, and expenses.',
    'commercial.raqib.problem1': 'Money movement is hard to see when records are scattered across notebooks, chats, and files.',
    'commercial.raqib.problem2': 'It is easy to lose track of who owes the business and what the business owes.',
    'commercial.raqib.problem3': 'Business expenses and income are often unclear without opening a heavy accounting system.',
    'commercial.raqib.problem4': 'Owners need a simple picture of financial position — not certified bookkeeping complexity.',
    'commercial.raqib.problem5': 'Retail POS and inventory tools do not replace calm financial monitoring for the business itself.',
    'commercial.raqib.feature1': 'Monitor money in and money out',
    'commercial.raqib.feature2': 'Follow income, expenses, and receivables',
    'commercial.raqib.feature3': 'Clearer view of what the business is owed and what it owes',
    'commercial.raqib.feature4': 'Simple business financial overview for owners and teams',
    'commercial.raqib.feature5': 'Financial monitoring without POS checkout',
    'commercial.raqib.feature6': 'Practical oversight — not certified accounting or ERP',
    'commercial.raqib.fit':
      'Need fast retail checkout? Choose {glasspos} or {iq-store}. Need currency-exchange operations? Choose {befx}. Raqib is for business financial monitoring — money, receivables, income, and expenses.',

    'commercial.befx.tagline': 'Specialized management for exchange operations, currencies, and vaults.',
    'commercial.befx.description':
      'BeFX is specialized software for exchange offices and currency-exchange businesses. It focuses on currency buying and selling, exchange rates, vault and cash movement for exchange-office operations — not generic financial monitoring.',
    'commercial.befx.problem1': 'Run currency buy and sell with clear rate control.',
    'commercial.befx.problem2': 'Track vault and cash movement for exchange desks.',
    'commercial.befx.problem3': 'Keep specialized exchange operations organized day to day.',
    'commercial.befx.feature1': 'Currency buy and sell for exchange offices',
    'commercial.befx.feature2': 'Exchange rate handling for desk operations',
    'commercial.befx.feature3': 'Vault and cash movement for exchange workflows',
    'commercial.befx.feature4': 'Specialized operations — not general business finance software',
    'commercial.befx.fit':
      'If your business is not a currency exchange operation and you mainly need financial business oversight, consider {raqib}.',
    'commercial.befx.thirdPartyRates':
      'Market data feeds or messaging services, when used, may carry their own provider charges.',

    'commercial.bepharma.tagline': 'Pharmacy operations with calm inventory control.',
    'commercial.bepharma.description':
      'BePharma is BeCoder’s pharmacy software — dispensing, inventory, and records without noise.',
    'commercial.bepharma.problem1': 'Keep pharmacy inventory accurate without slowing the counter.',
    'commercial.bepharma.problem2': 'Track dispensing and records in one place.',
    'commercial.bepharma.problem3': 'Run pharmacy operations without treating it as a generic grocery POS.',
    'commercial.bepharma.feature1': 'Pharmacy inventory control',
    'commercial.bepharma.feature2': 'Dispensing and records',
    'commercial.bepharma.feature3': 'Day-to-day pharmacy operations',
    'commercial.bepharma.feature4': 'Built for pharmacies — not a generic checkout clone',
    'commercial.bepharma.fit':
      'If you need fast general retail checkout, {glasspos} may be a simpler fit. BePharma is for pharmacy operations.',

    'commercial.fitTitle': 'Not sure this is the right fit?',

    'audience.supermarket': 'Supermarkets',
    'audience.minimarket': 'Mini markets',
    'audience.grocery': 'Grocery stores',
    'audience.convenience': 'Convenience stores',
    'audience.bakery': 'Bakeries',
    'audience.confectionery': 'Sweet & confectionery shops',
    'audience.beverage': 'Beverage shops',
    'audience.fastMovingRetail': 'Fast-moving retail counters',
    'audience.clothing': 'Clothing stores',
    'audience.shoes': 'Shoe stores',
    'audience.perfume': 'Perfume stores',
    'audience.cosmetics': 'Cosmetics & beauty stores',
    'audience.mobileShop': 'Mobile phone stores',
    'audience.mobileAccessories': 'Mobile accessories',
    'audience.electronics': 'Electronics stores',
    'audience.household': 'Household goods',
    'audience.homeware': 'Homeware stores',
    'audience.bookstore': 'Bookstores & stationery',
    'audience.spareParts': 'Spare parts stores',
    'audience.generalRetail': 'General retail',
    'audience.furniture': 'Furniture stores',
    'audience.appliances': 'Home appliance stores',
    'audience.wholesaler': 'Wholesalers',
    'audience.warehouse': 'Warehouses',
    'audience.distributor': 'Distributors',
    'audience.largeCatalog': 'Large or diverse catalogs',
    'audience.heavyInventory': 'Inventory-heavy retail',
    'audience.trading': 'Traders',
    'audience.serviceCo': 'Service companies',
    'audience.contractor': 'Contractors',
    'audience.office': 'Offices',
    'audience.factory': 'Factories',
    'audience.company': 'Companies',
    'audience.workshop': 'Workshops',
    'audience.sme': 'Small & medium businesses',
    'audience.businessOwner': 'Business owners',
    'audience.projectFinance': 'Project financial monitoring',
    'audience.exchangeOffice': 'Exchange offices',
    'audience.currencyExchange': 'Currency-exchange companies',
    'audience.multiCurrency': 'Multi-currency desks',
    'audience.exchangeBranches': 'Exchange businesses with vaults',
    'audience.exchangeVaultOps': 'Exchange vault & cash operations',

    'commercial.whoTitle': 'Who is this for?',
    'commercial.whoLede': 'Suitable for businesses like these — when the product fits your operations.',
    'commercial.explainTitle': 'How this product helps',
    'commercial.problemsTitle': 'What problems does it solve?',
    'commercial.pricingTitle': 'Pricing',
    'commercial.pricingLede':
      'Prices are shown for one market at a time. Iraq uses IQD. International uses USD. Changing language does not change the pricing market. These are independent list prices — not live currency conversion.',
    'commercial.pricingMarket': 'Pricing market',
    'commercial.market.iraq': 'Iraq (IQD)',
    'commercial.market.international': 'International (USD)',
    'commercial.market.iraqTab': 'Iraq',
    'commercial.market.internationalTab': 'International',
    'commercial.plan.cloud_annual': 'Cloud Services',
    'commercial.cloudNote':
      'Optional hosted services. If Cloud Services expire, your Lifetime software license stays valid, local software keeps working, and your data is not deleted.',
    'commercial.internationalCheckoutNote':
      'International USD prices are shown for transparency. Online USD checkout is not available yet. Contact sales.',
    'commercial.trialTitle': '3-Day Free Trial',
    'commercial.trialBody':
      'Try the full product for 3 days. No payment method required. One free trial per product, starting on first activation.',
    'commercial.trialComingSoon':
      'A free trial will be available when this product opens for customer trials. Pricing below shows what to expect.',
    'commercial.plan.monthly': 'Monthly',
    'commercial.plan.annual': 'Annual',
    'commercial.plan.lifetime': 'Lifetime License',
    'commercial.plan.saveApprox': 'About 17% less than 12× monthly',
    'commercial.plan.saveApproxDynamic': 'About {pct}% less than 12× monthly',
    'commercial.lifetimeBadge': '3 months of Premium Support included',
    'commercial.lifetimeNoMandatory': 'Cloud Services are optional — not required to keep the license',
    'commercial.lifetimeTitle': 'Lifetime License',
    'commercial.lifetimeLede': 'Pay once. Use forever.',
    'commercial.lifetimeBody':
      'A one-time license payment with a permanent right to use the licensed product. No mandatory monthly or annual renewal. Optional Cloud Services cover hosted sync, backup, and other server-dependent features only. Declining or not renewing Cloud Services does not end your Lifetime License, disable legitimate local/offline use, or delete your data. Three months of Premium Technical Support are included. After that, purchasing technical support is optional.',
    'commercial.supportTitle': 'Support & fixes',
    'commercial.supportBugsTitle': 'Bug fixes',
    'commercial.supportBugsBody':
      'Free. Any bug, defect, or malfunction caused by our existing software is fixed without additional charge. Bug fixes are not paid modules, and you do not need an active Cloud Services subscription to receive a correction for purchased functionality. This does not include unlimited custom development or consulting.',
    'commercial.supportSecurityTitle': 'Security fixes',
    'commercial.supportSecurityBody':
      'Free. Security defects attributable to our product are corrected without requiring a paid support plan.',
    'commercial.supportTechTitle': 'Technical support',
    'commercial.supportTechBody':
      'Monthly and Annual: included while your subscription is active. Lifetime: first 3 months included; after that, optional paid support may be offered later. We do not publish post–3-month support prices yet.',
    'commercial.supportCustomTitle': 'Custom development',
    'commercial.supportCustomBody':
      'Always quoted separately — customer-specific features, reports, integrations, workflows or bespoke modifications. Custom development is not the same as fixing a BeCoder product bug.',
    'commercial.thirdPartyTitle': 'Third-party & service costs',
    'commercial.thirdPartyBody':
      'A Lifetime License does not mean BeCoder absorbs every external recurring cost forever. When applicable, separate charges may apply for things like dedicated cloud hosting, SMS, WhatsApp or API services, paid third-party integrations, additional storage, or unusually large dedicated infrastructure — each with its own provider costs.',
    'commercial.faqTitle': 'Before you buy',
    'commercial.faqLede': 'Straight answers based on how BeCoder works today.',
    'commercial.updatesTitle': 'Updates',
    'commercial.updatesBody':
      'Product updates, bug fixes and security fixes continue for active Monthly and Annual subscriptions, and for Lifetime licenses as part of normal product maintenance for genuine product defects.',
    'commercial.ctaLede': 'Try when available, or talk with Sales about licensing.',

    'commercial.plan.monthly.f1': 'Recurring monthly license',
    'commercial.plan.monthly.f2': 'Full core product use while active',
    'commercial.plan.monthly.f3': 'Product updates, bug & security fixes',
    'commercial.plan.monthly.f4': 'Technical support while subscription is active',
    'commercial.plan.annual.f1': 'Same core benefits as Monthly',
    'commercial.plan.annual.f2': '12 months billed once — about 17% savings',
    'commercial.plan.annual.f3': 'Product updates, bug & security fixes',
    'commercial.plan.annual.f4': 'Technical support while subscription is active',
    'commercial.plan.lifetime.f1': 'Pay once. Use forever.',
    'commercial.plan.lifetime.f2': 'Permanent right to use the licensed product',
    'commercial.plan.lifetime.f3': 'No mandatory monthly or annual renewal',
    'commercial.plan.lifetime.f4': '3 months of Premium Technical Support included',
    'commercial.plan.lifetime.f5': 'Paid support after 3 months is optional — license stays valid',

    'plan.period.lifetime': 'lifetime',
    'plan.name.monthly': 'Monthly',
    'plan.name.annual': 'Annual',
    'plan.name.lifetime': 'Lifetime License',
    'plan.name.cloud_annual': 'Cloud Services',
    'showcase.plansPlaceholder': 'Prices in USD. Annual saves about 17% versus 12 months of Monthly.',

    'faq.what.q': 'What does this software do?',
    'faq.what.a': 'See the product overview and features on this page for the capabilities available today.',
    'faq.suitable.q': 'Is it suitable for my business?',
    'faq.suitable.a':
      'Check “Who is this for?” and the short fit guidance on this page. If your business type is listed and the description matches how you work, it is a strong fit — Contact Sales if you are unsure.',
    'faq.trial.q': 'Can I try it before buying?',
    'faq.trial.a':
      'Purchasable products offer a 3-day free trial when trial is available on BeCoder, so you can evaluate before purchasing.',
    'faq.afterTrial.q': 'What happens after the 3-day trial?',
    'faq.afterTrial.a':
      'Continue by choosing Monthly, Annual or Lifetime through BeCoder licensing. Contact Sales if you need help choosing.',
    'faq.monthlyVsAnnual.q': 'What is the difference between Monthly and Annual?',
    'faq.monthlyVsAnnual.a':
      'Same core product functionality. Annual is a billing discount — about 17% less than paying monthly for 12 months.',
    'faq.lifetime.q': 'What does Lifetime License mean?',
    'faq.lifetime.a':
      'Pay once for a permanent right to use the licensed product. No mandatory monthly or annual renewal. Three months of Premium Support are included.',
    'faq.lifetimeMonthly.q': 'Will I have to pay monthly after buying Lifetime?',
    'faq.lifetimeMonthly.a':
      'No mandatory monthly renewal for the Lifetime License itself. Optional paid support after the included 3 months does not replace the license.',
    'faq.afterSupport.q': 'What happens after the 3 months of free Lifetime support?',
    'faq.afterSupport.a':
      'Your Lifetime License remains valid. Purchasing technical support afterward is optional; we have not published those support prices yet.',
    'faq.supportMandatory.q': 'Is paid support mandatory?',
    'faq.supportMandatory.a':
      'No. Declining paid support after the included Lifetime period does not terminate the Lifetime License.',
    'faq.bugFixes.q': 'Are genuine bug fixes free?',
    'faq.bugFixes.a':
      'Yes. If a genuine defect is caused by our software, you are not charged simply to correct our bug.',
    'faq.securityFixes.q': 'Are security fixes free?',
    'faq.securityFixes.a':
      'Yes. Security defects attributable to our product are corrected without requiring a paid support plan.',
    'faq.customDev.q': 'What counts as custom development?',
    'faq.customDev.a':
      'Customer-specific features, reports, integrations, workflows or bespoke modifications — always quoted separately, and not the same as fixing a product bug.',
    'faq.thirdParty.q': 'Are third-party services included?',
    'faq.thirdParty.a':
      'Not automatically. Dedicated hosting, SMS, WhatsApp/API services, paid integrations, extra storage or large infrastructure may carry separate provider costs when used.',

    'faq.raqibWhat.q': 'What is Raqib?',
    'faq.raqibWhat.a':
      'Raqib is BeCoder’s financial monitoring product for business finances — money movement, income, expenses, and receivables. It is preparing for customer launch (coming soon). It is not a POS, inventory system, exchange desk, or certified accounting/ERP suite.',
    'faq.raqibCompaniesOnly.q': 'Is Raqib only for companies?',
    'faq.raqibCompaniesOnly.a':
      'Raqib is positioned for business use: companies, contractors, offices, traders, distributors, workshops, SMEs, and business owners who need clearer financial monitoring.',
    'faq.raqibPersonal.q': 'Can I use Raqib for personal or family expenses?',
    'faq.raqibPersonal.a':
      'Not as a published capability today. The current public positioning is business financial monitoring. Personal and family finance modes are not offered on this page as available features.',
    'faq.raqibIOwe.q': 'Can I record money I owe someone?',
    'faq.raqibIOwe.a':
      'Raqib’s published positioning includes clearer visibility of what the business owes, as part of financial monitoring. Detailed personal debt ledgers and installment workflows are not advertised as available features yet.',
    'faq.raqibOwesMe.q': 'Can I record money someone owes me?',
    'faq.raqibOwesMe.a':
      'Receivables — money owed to the business — are part of Raqib’s financial monitoring positioning. Exact recording workflows will match the shipping customer release when the product opens.',
    'faq.raqibPartial.q': 'Can I track partial debt payments and remaining balances?',
    'faq.raqibPartial.a':
      'Not as a published capability today. Partial-payment debt tracking, remaining balances, and payment history are not claimed on this page as available features.',
    'faq.raqibSubscriptions.q': 'Can I track subscriptions and recurring obligations?',
    'faq.raqibSubscriptions.a':
      'Not as a published capability today. Subscription and recurring-bill tracking (internet, rent, installments, and similar) is not claimed on this page as an available feature.',
    'faq.raqibBusinessMoney.q': 'Can I use it to monitor business income and expenses?',
    'faq.raqibBusinessMoney.a':
      'Yes — that is central to Raqib’s published purpose: monitoring business money movement, income, and expenses for clearer owner oversight, without certified accounting complexity.',
    'faq.raqibIsPos.q': 'Is Raqib a POS system?',
    'faq.raqibIsPos.a':
      'No. For fast daily retail checkout, see GlassPOS. For catalog- and inventory-heavy retail, see IQ Store.',
    'faq.raqibIsInventory.q': 'Is Raqib an inventory system?',
    'faq.raqibIsInventory.a':
      'No. Product and inventory organization belongs to IQ Store (and related retail tools), not Raqib.',
    'faq.raqibIsErp.q': 'Is Raqib a full accounting or ERP system?',
    'faq.raqibIsErp.a':
      'No. Raqib is financial monitoring and business money oversight — not certified accounting, tax, audit, or ERP software.',
    'faq.raqibVsOthers.q': 'What is the difference between Raqib, GlassPOS, and IQ Store?',
    'faq.raqibVsOthers.a':
      'GlassPOS: fast-moving retail POS. IQ Store: product/catalog/inventory-heavy retail. Raqib: business financial monitoring — money, receivables, income, and expenses. BeFX covers currency-exchange operations.',
  },
  ar: {
    'product.display.glasspos': 'GlassPOS',
    'product.display.iqStore': 'IQ Store',
    'product.display.raqib': 'راقب',
    'product.display.befx': 'BeFX',
    'product.display.ledger': 'راقب',
    'product.display.bepharma': 'BePharma',

    'commercial.glasspos.tagline': 'بع بسرعة، راقب المخزون، واعرف مبيعاتك اليومية.',
    'commercial.glasspos.description':
      'GlassPOS نظام نقطة بيع عملي وسريع للأعمال ذات البيع اليومي المتكرر عند الكاونتر. يركّز على المحاسبة السريعة، والبيع بالباركود، والمنتجات، ورؤية المخزون، والعملاء، وتقارير المبيعات اليومية — للمتاجر التي تكون سرعة البيع فيها الأولوية.',
    'commercial.glasspos.problem1': 'سرّع محاسبة الكاونتر في أيام البيع المزدحمة.',
    'commercial.glasspos.problem2': 'بِع بالباركود مع إبقاء المخزون والعملاء واضحين.',
    'commercial.glasspos.problem3': 'اعرف مبيعاتك اليومية دون إبطاء الكاونتر.',
    'commercial.glasspos.feature1': 'محاسبة يومية سريعة وبيع بالباركود',
    'commercial.glasspos.feature2': 'منتجات ورؤية مخزون وعملاء',
    'commercial.glasspos.feature3': 'تقارير مبيعات يومية واضحة',
    'commercial.glasspos.feature4': 'مصمم لكاونترات التجزئة سريعة الحركة',
    'commercial.glasspos.fit':
      'إذا كان عملك يحتاج تنظيماً أعمق للكتالوج والمخزون أكثر من سرعة المحاسبة، فقد يناسبك {iq-store} أكثر.',

    'commercial.iqStore.tagline': 'نظّم منتجاتك ومخزونك ومتجرك من مكان واحد.',
    'commercial.iqStore.description':
      'IQ Store للأعمال التي تكون فيها إدارة المنتجات والمخزون وهيكل الكتالوج وعمليات المتجر أهم أو أعقد من مجرد محاسبة سريعة. يساعدك على تنظيم الكتالوجات الكبيرة وتجزئة المخزون الكثيف من مكان واحد — وليس نسخة أخرى من نفس نقطة البيع.',
    'commercial.iqStore.problem1': 'هيّئ كتالوجات كبيرة أو متنوعة دون فقدان السيطرة.',
    'commercial.iqStore.problem2': 'أبقِ المخزون وعمليات المتجر منظمة مع نمو الكتالوج.',
    'commercial.iqStore.problem3': 'أدر تجزئة ثقيلة المنتجات دون اختزال كل عملية في بيع باركود بسيط.',
    'commercial.iqStore.feature1': 'تنظيم المنتجات والكتالوج',
    'commercial.iqStore.feature2': 'رؤية المخزون للمتاجر الأكثر تعقيداً',
    'commercial.iqStore.feature3': 'عمليات متجر أبعد من المحاسبة الأساسية',
    'commercial.iqStore.feature4': 'مصمم لتجزئة ثقيلة المنتجات والمخزون',
    'commercial.iqStore.fit':
      'إذا كانت أولويتك محاسبة يومية سريعة عند الكاونتر، فقد يكون {glasspos} الخيار الأبسط.',

    'commercial.raqib.tagline': 'راقب — كل التزاماتك المالية في مكان واحد.',
    'commercial.raqib.description':
      'راقب تطبيق لمراقبة الوضع المالي للأعمال على BeCoder. يساعدك على متابعة حركة المال والإيرادات والمصروفات والذمم — لتعرف ما لك وما عليك، وترى صورة أوضح لوين تروح فلوس العمل. ليس نقطة بيع أو كاشير، وليس نظام مخزون أو كتالوج، وليس مكتب صرافة، وليس محاسبة معتمدة أو نظام ERP ثقيل. راقب قيد التحضير لإطلاقه للعملاء على BeCoder.',
    'commercial.raqib.whoLede':
      'لأصحاب الأعمال والفرق التي تحتاج مراقبة مالية أوضح — وليس كاونتر بيع تجزئة.',
    'commercial.raqib.benefitsTitle': 'أبرز الفوائد',
    'commercial.raqib.explainHeading': 'تعرّف على راقب',
    'commercial.raqib.explain.what.title': 'ما هو راقب؟',
    'commercial.raqib.explain.what.body':
      'راقب مراقبة مالية بسيطة ومتابعة لأموال العمل. تابع دخول المال وخروجه، وأبقِ الإيرادات والمصروفات واضحة، وكن على اطلاع بالذمم وما على العمل — دون تعقيد برامج المحاسبة المعتمدة.',
    'commercial.raqib.explain.debts.title': 'الذمم وما عليك',
    'commercial.raqib.explain.debts.body':
      'من أهداف راقب أن تكون الأموال المستحقة للعمل (الذمم) وما على العمل أوضح للمشاهدة. الهدف نظرة عملية لما لك وما عليك — وليست دفتر محاسبة كامل أو كشوف معتمدة.',
    'commercial.raqib.explain.business.title': 'إيرادات ومصروفات العمل',
    'commercial.raqib.explain.business.body':
      'راقب موجّه لمساعدة المالك على فهم حركة مال العمل: الإيرادات المستلمة، ومصروفات التشغيل، وصورة أبسط للوضع المالي. يناسب الشركات والمقاولين والمكاتب والتجار والموزعين والورش والمنشآت الصغيرة والمتوسطة وأصحاب الأعمال.',
    'commercial.raqib.explain.not.title': 'ما الذي لا يقدّمه راقب؟',
    'commercial.raqib.explain.not.body':
      'راقب ليس نقطة بيع أو كاشير، وليس برنامج منتجات/مخزون للتجزئة، وليس نظام عمليات صرافة، وليس محاسبة معتمدة أو ضرائب أو تدقيق أو ERP.',
    'commercial.raqib.explain.diff.title': 'كيف يختلف عن منتجات BeCoder الأخرى؟',
    'commercial.raqib.explain.diff.body':
      'GlassPOS لمحاسبة التجزئة اليومية السريعة. IQ Store لتجزئة ثقيلة المنتجات والمخزون. BeFX لمكاتب الصرافة. راقب لمراقبة مال العمل والذمم والإيرادات والمصروفات.',
    'commercial.raqib.problem1': 'حركة المال صعبة المتابعة عندما تتوزع السجلات بين دفاتر ومحادثات وملفات.',
    'commercial.raqib.problem2': 'يسهل نسيان من عليه للعمل وما على العمل من التزامات.',
    'commercial.raqib.problem3': 'مصروفات العمل وإيراداته غالباً غير واضحة دون فتح نظام محاسبة ثقيل.',
    'commercial.raqib.problem4': 'المالك يحتاج صورة بسيطة للوضع المالي — لا تعقيد مسك دفاتر معتمد.',
    'commercial.raqib.problem5': 'أدوات نقاط البيع والمخزون لا تغني عن مراقبة مالية هادئة للعمل نفسه.',
    'commercial.raqib.feature1': 'مراقبة دخول المال وخروجه',
    'commercial.raqib.feature2': 'متابعة الإيرادات والمصروفات والذمم',
    'commercial.raqib.feature3': 'رؤية أوضح لما لك وما عليك في العمل',
    'commercial.raqib.feature4': 'نظرة مالية بسيطة للمالكين والفرق',
    'commercial.raqib.feature5': 'مراقبة مالية بلا محاسبة كاشير',
    'commercial.raqib.feature6': 'متابعة عملية — وليست محاسبة معتمدة أو ERP',
    'commercial.raqib.fit':
      'تحتاج محاسبة تجزئة سريعة؟ اختر {glasspos} أو {iq-store}. تحتاج عمليات صرافة؟ اختر {befx}. راقب لمراقبة الوضع المالي للعمل — المال والذمم والإيرادات والمصروفات.',

    'commercial.befx.tagline': 'إدارة متخصصة لعمليات الصرافة والعملات والخزائن.',
    'commercial.befx.description':
      'BeFX برنامج متخصص لمكاتب الصرافة وأعمال تبديل العملات. يركّز على شراء وبيع العملات وأسعار الصرف وحركة الخزنة والنقد لعمليات مكاتب الصرافة — وليس مراقبة مالية عامة للأعمال.',
    'commercial.befx.problem1': 'شغّل شراء وبيع العملات بتحكم واضح بالأسعار.',
    'commercial.befx.problem2': 'تتبع حركة الخزنة والنقد لمكاتب الصرافة.',
    'commercial.befx.problem3': 'حافظ على تنظيم عمليات الصرافة المتخصصة يومياً.',
    'commercial.befx.feature1': 'شراء وبيع العملات لمكاتب الصرافة',
    'commercial.befx.feature2': 'إدارة أسعار الصرف لعمليات المكتب',
    'commercial.befx.feature3': 'حركة الخزنة والنقد لمسارات الصرافة',
    'commercial.befx.feature4': 'عمليات متخصصة — وليست برنامجاً مالياً عاماً',
    'commercial.befx.fit':
      'إذا لم يكن عملك مكتباً للصرافة وكنت تحتاج أساساً متابعة مالية للعمل، فكّر في {raqib}.',
    'commercial.befx.thirdPartyRates':
      'قد تترتب رسوم مزوّد عند استخدام موجزات بيانات السوق أو خدمات الرسائل.',

    'commercial.bepharma.tagline': 'تشغيل الصيدلية مع ضبط هادئ للمخزون.',
    'commercial.bepharma.description':
      'BePharma برنامج الصيدلة من BeCoder — صرف، مخزون، وسجلات بلا ضجيج.',
    'commercial.bepharma.problem1': 'أبقِ مخزون الصيدلية دقيقاً دون إبطاء الكاونتر.',
    'commercial.bepharma.problem2': 'تابع الصرف والسجلات من مكان واحد.',
    'commercial.bepharma.problem3': 'أدر عمليات الصيدلية دون معاملتها كنقطة بيع بقالة عامة.',
    'commercial.bepharma.feature1': 'ضبط مخزون الصيدلية',
    'commercial.bepharma.feature2': 'الصرف والسجلات',
    'commercial.bepharma.feature3': 'عمليات الصيدلية اليومية',
    'commercial.bepharma.feature4': 'مصمم للصيدليات — وليس نسخة كاشير عامة',
    'commercial.bepharma.fit':
      'إذا كنت تحتاج محاسبة تجزئة عامة سريعة، قد يناسبك {glasspos} أكثر. BePharma لعمليات الصيدلية.',

    'commercial.fitTitle': 'لست متأكداً أنه الأنسب؟',

    'audience.supermarket': 'سوبرماركت',
    'audience.minimarket': 'ميني ماركت',
    'audience.grocery': 'بقالات',
    'audience.convenience': 'متاجر القرب والخدمة السريعة',
    'audience.bakery': 'مخابز',
    'audience.confectionery': 'محلات حلويات ومعجنات',
    'audience.beverage': 'محلات مشروبات',
    'audience.fastMovingRetail': 'تجزئة سريعة عند الكاونتر',
    'audience.clothing': 'محلات ملابس',
    'audience.shoes': 'محلات أحذية',
    'audience.perfume': 'محلات عطور',
    'audience.cosmetics': 'محلات تجميل ومستحضرات',
    'audience.mobileShop': 'محلات جوالات',
    'audience.mobileAccessories': 'إكسسوارات الجوال',
    'audience.electronics': 'محلات إلكترونيات',
    'audience.household': 'منزلية',
    'audience.homeware': 'محلات أدوات منزلية',
    'audience.bookstore': 'مكتبات وقرطاسية',
    'audience.spareParts': 'محلات قطع غيار',
    'audience.generalRetail': 'تجزئة عامة',
    'audience.furniture': 'محلات أثاث',
    'audience.appliances': 'محلات أجهزة منزلية',
    'audience.wholesaler': 'جملة',
    'audience.warehouse': 'مستودعات',
    'audience.distributor': 'موزّعون',
    'audience.largeCatalog': 'كتالوجات كبيرة أو متنوعة',
    'audience.heavyInventory': 'تجزئة كثيفة المخزون',
    'audience.trading': 'تجّار',
    'audience.serviceCo': 'شركات خدمات',
    'audience.contractor': 'مقاولون',
    'audience.office': 'مكاتب',
    'audience.factory': 'مصانع',
    'audience.company': 'شركات',
    'audience.workshop': 'ورش',
    'audience.sme': 'أعمال صغيرة ومتوسطة',
    'audience.businessOwner': 'أصحاب أعمال',
    'audience.projectFinance': 'مراقبة مالية للمشاريع',
    'audience.exchangeOffice': 'مكاتب صرافة',
    'audience.currencyExchange': 'شركات تبديل عملات',
    'audience.multiCurrency': 'مكاتب متعددة العملات',
    'audience.exchangeBranches': 'صرافة مع خزائن',
    'audience.exchangeVaultOps': 'عمليات خزنة ونقد للصرافة',

    'commercial.whoTitle': 'لمن هذا المنتج؟',
    'commercial.whoLede': 'مناسب لأعمال مثل هذه — عندما يلائم طريقة عملك.',
    'commercial.explainTitle': 'كيف يساعدك هذا المنتج',
    'commercial.problemsTitle': 'ما المشكلات التي يحلها؟',
    'commercial.pricingTitle': 'الأسعار',
    'commercial.pricingLede':
      'تُعرض الأسعار لسوق واحد في كل مرة. العراق بالدينار العراقي. الدولي بالدولار. تغيير اللغة لا يغيّر سوق التسعير. هذه أسعار قائمة مستقلة — وليست تحويلاً لحظياً للعملة.',
    'commercial.pricingMarket': 'سوق التسعير',
    'commercial.market.iraq': 'العراق (IQD)',
    'commercial.market.international': 'دولي (USD)',
    'commercial.market.iraqTab': 'العراق',
    'commercial.market.internationalTab': 'دولي',
    'commercial.plan.cloud_annual': 'خدمات السحابة',
    'commercial.cloudNote':
      'خدمات مستضافة اختيارية. إذا انتهت خدمات السحابة، يبقى ترخيص مدى الحياة صالحاً، ويعمل البرنامج المحلي، ولا تُحذف بياناتك.',
    'commercial.internationalCheckoutNote':
      'أسعار الدولار الدولية معروضة للشفافية. الدفع الإلكتروني بالدولار غير متاح بعد. تواصل مع المبيعات.',
    'commercial.trialTitle': 'تجربة مجانية لمدة 3 أيام',
    'commercial.trialBody':
      'جرّب المنتج بالكامل لمدة 3 أيام. لا تحتاج إلى وسيلة دفع. تجربة مجانية واحدة لكل منتج، وتبدأ عند أول تفعيل.',
    'commercial.trialComingSoon':
      'ستتوفر تجربة مجانية عند فتح المنتج لتجارب العملاء. الأسعار أدناه توضّح ما يمكن توقعه.',
    'commercial.plan.monthly': 'شهري',
    'commercial.plan.annual': 'سنوي',
    'commercial.plan.lifetime': 'ترخيص مدى الحياة',
    'commercial.plan.saveApprox': 'أوفر بنحو 17٪ من 12× الشهري',
    'commercial.plan.saveApproxDynamic': 'أوفر بنحو {pct}٪ من 12× الشهري',
    'commercial.lifetimeBadge': '3 أشهر دعم فني مميز مشمولة',
    'commercial.lifetimeNoMandatory': 'خدمات السحابة اختيارية — ليست شرطاً لبقاء الترخيص',
    'commercial.lifetimeTitle': 'ترخيص مدى الحياة',
    'commercial.lifetimeLede': 'ادفع مرة. استخدم للأبد.',
    'commercial.lifetimeBody':
      'دفعة ترخيص لمرة واحدة مع حق دائم لاستخدام المنتج المرخّص. بلا تجديد شهري أو سنوي إلزامي. خدمات السحابة الاختيارية تغطي المزامنة والنسخ الاحتياطي المستضاف والخدمات التي تعتمد على خوادمنا فقط. عدم تجديد خدمات السحابة لا يُنهي ترخيص مدى الحياة، ولا يعطّل الاستخدام المحلي المشروع، ولا يحذف بياناتك. يشمل ثلاثة أشهر من الدعم الفني المميز. بعد ذلك يكون شراء الدعم اختيارياً.',
    'commercial.supportTitle': 'الدعم والإصلاحات',
    'commercial.supportBugsTitle': 'إصلاح الأخطاء',
    'commercial.supportBugsBody':
      'مجاني. أي خطأ أو عيب أو خلل تسبّبه برمجياتنا الحالية يُصلح دون رسوم إضافية. إصلاح الأخطاء ليس وحدة مدفوعة، ولا تحتاج اشتراك خدمات سحابة نشطاً لتصحيح وظيفة اشتريتها. هذا لا يشمل تطويراً مخصصاً أو استشارات بلا حدود.',
    'commercial.supportSecurityTitle': 'إصلاحات الأمان',
    'commercial.supportSecurityBody':
      'مجاني. تُصحَّح ثغرات الأمان المنسوبة لمنتجنا دون اشتراط خطة دعم مدفوعة.',
    'commercial.supportTechTitle': 'الدعم الفني',
    'commercial.supportTechBody':
      'الشهري والسنوي: مشمول أثناء الاشتراك النشط. مدى الحياة: أول 3 أشهر مشمولة؛ بعدها قد يُعرض دعم مدفوع اختيارياً لاحقاً. لم ننشر بعد أسعار الدعم بعد الأشهر الثلاثة.',
    'commercial.supportCustomTitle': 'تطوير مخصص',
    'commercial.supportCustomBody':
      'يُسعَّر دائماً بشكل منفصل — ميزات أو تقارير أو تكاملات أو سير عمل خاصة بالعميل. التطوير المخصص ليس إصلاح خطأ في منتج BeCoder.',
    'commercial.thirdPartyTitle': 'تكاليف الطرف الثالث والخدمات',
    'commercial.thirdPartyBody':
      'ترخيص مدى الحياة لا يعني أن BeCoder يتحمل كل تكلفة خارجية متكررة إلى الأبد. عند الاقتضاء قد تُطبَّق رسوم منفصلة مثل الاستضافة السحابية المخصصة أو الرسائل القصيرة أو واتساب/واجهات البرمجة أو التكاملات المدفوعة أو التخزين الإضافي أو بنية تحتية كبيرة — ولكل منها تكاليف مزوّدها.',
    'commercial.faqTitle': 'قبل الشراء',
    'commercial.faqLede': 'إجابات مباشرة وفق عمل BeCoder اليوم.',
    'commercial.updatesTitle': 'التحديثات',
    'commercial.updatesBody':
      'تستمر تحديثات المنتج وإصلاحات الأخطاء والأمان للاشتراكات الشهرية والسنوية النشطة، ولترخيص مدى الحياة ضمن الصيانة الاعتيادية للعيوب الحقيقية في المنتج.',
    'commercial.ctaLede': 'جرّب عند التوفر، أو تواصل مع المبيعات للترخيص.',

    'commercial.plan.monthly.f1': 'ترخيص شهري متجدد',
    'commercial.plan.monthly.f2': 'استخدام كامل للوظائف الأساسية أثناء النشاط',
    'commercial.plan.monthly.f3': 'تحديثات المنتج وإصلاحات الأخطاء والأمان',
    'commercial.plan.monthly.f4': 'دعم فني أثناء الاشتراك النشط',
    'commercial.plan.annual.f1': 'نفس المنافع الأساسية للشهري',
    'commercial.plan.annual.f2': '12 شهراً بدفعة واحدة — توفير بنحو 17٪',
    'commercial.plan.annual.f3': 'تحديثات المنتج وإصلاحات الأخطاء والأمان',
    'commercial.plan.annual.f4': 'دعم فني أثناء الاشتراك النشط',
    'commercial.plan.lifetime.f1': 'ادفع مرة. استخدم للأبد.',
    'commercial.plan.lifetime.f2': 'حق دائم لاستخدام المنتج المرخّص',
    'commercial.plan.lifetime.f3': 'بلا تجديد شهري أو سنوي إلزامي',
    'commercial.plan.lifetime.f4': '3 أشهر دعم فني مميز مشمولة',
    'commercial.plan.lifetime.f5': 'الدعم المدفوع بعد 3 أشهر اختياري — الترخيص يبقى سارياً',

    'plan.period.lifetime': 'مدى الحياة',
    'plan.name.monthly': 'شهري',
    'plan.name.annual': 'سنوي',
    'plan.name.lifetime': 'ترخيص مدى الحياة',
    'plan.name.cloud_annual': 'خدمات السحابة',
    'showcase.plansPlaceholder': 'الأسعار بالدولار. السنوي يوفر نحو 17٪ مقارنة بـ 12 شهراً شهرياً.',

    'faq.what.q': 'ماذا يفعل هذا البرنامج؟',
    'faq.what.a': 'راجع نظرة المنتج والميزات في هذه الصفحة للقدرات المتاحة اليوم.',
    'faq.suitable.q': 'هل يناسب نوع عملي؟',
    'faq.suitable.a':
      'راجع قسم «لمن هذا المنتج؟» وإرشاد الملاءمة القصير في هذه الصفحة. إذا كان نوع عملك مدرجاً والوصف يطابق طريقة عملك فهو ملاءمة قوية — تواصل مع المبيعات إن كنت غير متأكد.',
    'faq.trial.q': 'هل يمكنني التجربة قبل الشراء؟',
    'faq.trial.a':
      'المنتجات القابلة للشراء تقدّم تجربة مجانية لمدة 3 أيام عندما تكون التجربة متاحة على BeCoder لتقييم المنتج قبل الشراء.',
    'faq.afterTrial.q': 'ماذا يحدث بعد انتهاء التجربة المجانية لمدة 3 أيام؟',
    'faq.afterTrial.a':
      'تابع عبر اختيار شهري أو سنوي أو مدى الحياة عبر ترخيص BeCoder. تواصل مع المبيعات للمساعدة في الاختيار.',
    'faq.monthlyVsAnnual.q': 'ما الفرق بين الشهري والسنوي؟',
    'faq.monthlyVsAnnual.a':
      'نفس الوظائف الأساسية للمنتج. السنوي خصم فوترة — أوفر بنحو 17٪ من دفع الشهري لمدة 12 شهراً.',
    'faq.lifetime.q': 'ماذا يعني ترخيص مدى الحياة؟',
    'faq.lifetime.a':
      'ادفع مرة لحق دائم في استخدام المنتج المرخّص. بلا تجديد شهري أو سنوي إلزامي. يشمل ثلاثة أشهر من الدعم المميز.',
    'faq.lifetimeMonthly.q': 'هل أدفع شهرياً بعد شراء مدى الحياة؟',
    'faq.lifetimeMonthly.a':
      'لا يوجد تجديد شهري إلزامي لترخيص مدى الحياة نفسه. الدعم المدفوع الاختياري بعد الأشهر الثلاثة لا يحل محل الترخيص.',
    'faq.afterSupport.q': 'ماذا بعد انتهاء 3 أشهر الدعم المجاني لمدى الحياة؟',
    'faq.afterSupport.a':
      'يبقى ترخيص مدى الحياة سارياً. شراء الدعم الفني بعد ذلك اختياري؛ لم ننشر بعد أسعار ذلك الدعم.',
    'faq.supportMandatory.q': 'هل الدعم المدفوع إلزامي؟',
    'faq.supportMandatory.a':
      'لا. رفض الدعم المدفوع بعد الفترة المشمولة في مدى الحياة لا ينهي الترخيص.',
    'faq.bugFixes.q': 'هل إصلاح الأخطاء الحقيقية مجاني؟',
    'faq.bugFixes.a':
      'نعم. إذا كان الخلل ناتجاً عن برنامجنا، لا تُحاسب لمجرد تصحيح خطئنا.',
    'faq.securityFixes.q': 'هل إصلاحات الأمان مجانية؟',
    'faq.securityFixes.a':
      'نعم. تُصحَّح ثغرات الأمان المنسوبة لمنتجنا دون اشتراط خطة دعم مدفوعة.',
    'faq.customDev.q': 'ما الذي يُعد تطويراً مخصصاً؟',
    'faq.customDev.a':
      'ميزات أو تقارير أو تكاملات أو سير عمل خاصة بالعميل — تُسعَّر دائماً بشكل منفصل، وليست إصلاح خطأ في المنتج.',
    'faq.thirdParty.q': 'هل خدمات الطرف الثالث مشمولة؟',
    'faq.thirdParty.a':
      'ليس تلقائياً. الاستضافة المخصصة أو الرسائل القصيرة أو واتساب/واجهات البرمجة أو التكاملات المدفوعة أو التخزين الإضافي أو البنية الكبيرة قد تحمل تكاليف مزوّد منفصلة عند استخدامها.',

    'faq.raqibWhat.q': 'ما هو راقب؟',
    'faq.raqibWhat.a':
      'راقب منتج BeCoder لمراقبة الوضع المالي للأعمال — حركة المال والإيرادات والمصروفات والذمم. قيد التحضير لإطلاقه للعملاء (قريباً). ليس نقطة بيع، ولا نظام مخزون، ولا مكتب صرافة، ولا محاسبة معتمدة أو ERP.',
    'faq.raqibCompaniesOnly.q': 'هل راقب للشركات فقط؟',
    'faq.raqibCompaniesOnly.a':
      'راقب موجّه للاستخدام التجاري: الشركات والمقاولون والمكاتب والتجار والموزعون والورش والمنشآت الصغيرة والمتوسطة وأصحاب الأعمال الذين يحتاجون مراقبة مالية أوضح.',
    'faq.raqibPersonal.q': 'هل يمكن استخدام راقب للمصاريف الشخصية أو العائلية؟',
    'faq.raqibPersonal.a':
      'ليس كقدرة منشورة اليوم. التموضع الحالي مراقبة مالية للأعمال. أوضاع المالية الشخصية والعائلية غير معروضة في هذه الصفحة كميزات متاحة.',
    'faq.raqibIOwe.q': 'هل أسجّل مالاً عليّ لشخص آخر؟',
    'faq.raqibIOwe.a':
      'تموضع راقب المنشور يشمل رؤية أوضح لما على العمل ضمن المراقبة المالية. دفاتر الديون الشخصية التفصيلية ومسارات الأقساط غير معلَن عنها كميزات متاحة بعد.',
    'faq.raqibOwesMe.q': 'هل أسجّل مالاً لي على شخص آخر؟',
    'faq.raqibOwesMe.a':
      'الذمم — المال المستحق للعمل — جزء من تموضع المراقبة المالية لراقب. تفاصيل التسجيل ستطابق إصدار العملاء عند فتح المنتج.',
    'faq.raqibPartial.q': 'هل أتابع دفعات جزئية على دين والرصيد المتبقي؟',
    'faq.raqibPartial.a':
      'ليس كقدرة منشورة اليوم. تتبع الدفعات الجزئية والأرصدة المتبقية وسجل التحصيل غير مُعلَن في هذه الصفحة كميزات متاحة.',
    'faq.raqibSubscriptions.q': 'هل أتابع الاشتراكات والالتزامات المتكررة؟',
    'faq.raqibSubscriptions.a':
      'ليس كقدرة منشورة اليوم. متابعة اشتراكات الفواتير والالتزامات المتكررة (إنترنت، إيجار، أقساط وما شابه) غير مُعلَنة في هذه الصفحة كميزة متاحة.',
    'faq.raqibBusinessMoney.q': 'هل أستخدمه لمراقبة إيرادات ومصروفات العمل؟',
    'faq.raqibBusinessMoney.a':
      'نعم — هذا محور غرض راقب المنشور: مراقبة حركة مال العمل والإيرادات والمصروفات لرؤية أوضح للمالك، دون تعقيد محاسبة معتمدة.',
    'faq.raqibIsPos.q': 'هل راقب نظام نقطة بيع؟',
    'faq.raqibIsPos.a':
      'لا. لمحاسبة التجزئة اليومية السريعة راجع GlassPOS. لتجزئة ثقيلة المنتجات والمخزون راجع IQ Store.',
    'faq.raqibIsInventory.q': 'هل راقب نظام مخزون؟',
    'faq.raqibIsInventory.a':
      'لا. تنظيم المنتجات والمخزون يخص IQ Store (وأدوات التجزئة ذات الصلة)، وليس راقب.',
    'faq.raqibIsErp.q': 'هل راقب نظام محاسبة أو ERP كامل؟',
    'faq.raqibIsErp.a':
      'لا. راقب مراقبة مالية ومتابعة لأموال العمل — وليس محاسبة معتمدة أو ضرائب أو تدقيق أو ERP.',
    'faq.raqibVsOthers.q': 'ما الفرق بين راقب وGlassPOS وIQ Store؟',
    'faq.raqibVsOthers.a':
      'GlassPOS: نقطة بيع للتجزئة سريعة الحركة. IQ Store: تجزئة ثقيلة المنتجات/المخزون. راقب: مراقبة مالية للعمل — المال والذمم والإيرادات والمصروفات. BeFX لعمليات الصرافة.',
  },
  ckb: {
    'product.display.glasspos': 'GlassPOS',
    'product.display.iqStore': 'IQ Store',
    'product.display.raqib': 'Raqb',
    'product.display.befx': 'BeFX',
    'product.display.ledger': 'Raqb',
    'product.display.bepharma': 'BePharma',

    'commercial.glasspos.tagline': 'خێراتر بفرۆشە، کۆگا چاودێری بکە، و فرۆشتنی ڕۆژانەت بزانە.',
    'commercial.glasspos.description':
      'GlassPOS سیستەمێکی خاڵی فرۆشتنی خێرا و کردارییە بۆ کاروبار کە فرۆشتنی ڕۆژانەی زۆریان لەسەر کاونتەر هەیە. سەرنج دەخاتە سەر پارەدانی خێرا، فرۆشتن بە بارکۆد، بەرهەم، بینینی کۆگا، کڕیار و ڕاپۆرتی فرۆشتنی ڕۆژانە — بۆ ئەو فرۆشگایانەی خێرایی فرۆشتن ئەولەویەتە.',
    'commercial.glasspos.problem1': 'پارەدانی کاونتەر خێراتر بکە لە ڕۆژانی فرۆشتنی قەرەباڵغ.',
    'commercial.glasspos.problem2': 'بە بارکۆد بفرۆشە و کۆگا و کڕیار بە ڕوونی بمێنێتەوە.',
    'commercial.glasspos.problem3': 'فرۆشتنی ڕۆژانەت بزانە بەبێ خاوکردنەوەی کاونتەر.',
    'commercial.glasspos.feature1': 'پارەدانی خێرای ڕۆژانە و فرۆشتن بە بارکۆد',
    'commercial.glasspos.feature2': 'بەرهەم، بینینی کۆگا و کڕیار',
    'commercial.glasspos.feature3': 'ڕاپۆرتی ڕوونی فرۆشتنی ڕۆژانە',
    'commercial.glasspos.feature4': 'بۆ کاونتەری فرۆشتنی خێراجووڵە',
    'commercial.glasspos.fit':
      'ئەگەر کاروبارەکەت پێویستی بە ڕێکخستنی قووڵتری کەتەلۆگ و کۆگا هەیە نەک تەنها پارەدانی خێرا، لەوانەیە {iq-store} گونجاوتر بێت.',

    'commercial.iqStore.tagline': 'بەرهەم، کۆگا و فرۆشگاکەت لە یەک شوێن ڕێکبخە.',
    'commercial.iqStore.description':
      'IQ Store بۆ ئەو کاروبارانەیە کە بەڕێوەبردنی بەرهەم، کۆگا، پێکهاتەی کەتەلۆگ و کارەکانی فرۆشگا گرنگتر یان ئاڵۆزترە لە تەنها پارەدانی خێرا. یارمەتیت دەدات کەتەلۆگی گەورە و فرۆشتنی قورسی کۆگا لە یەک شوێن ڕێکبخەیت — نەک وەشانێکی دیکەی هەمان POS.',
    'commercial.iqStore.problem1': 'کەتەلۆگی گەورە یان هەمەجۆر ڕێکبخە بەبێ لەدەستدانی کۆنترۆڵ.',
    'commercial.iqStore.problem2': 'کۆگا و کارەکانی فرۆشگا ڕێکبخە کاتێک کەتەلۆگ گەورە دەبێت.',
    'commercial.iqStore.problem3': 'فرۆشتنی قورسی بەرهەم بەڕێوەببە بەبێ ئەوەی هەموو فرۆشتنێک وەک پارەدانی بارکۆدی سادە ببینیت.',
    'commercial.iqStore.feature1': 'ڕێکخستنی بەرهەم و کەتەلۆگ',
    'commercial.iqStore.feature2': 'بینینی کۆگا بۆ فرۆشگای ئاڵۆزتر',
    'commercial.iqStore.feature3': 'کارەکانی فرۆشگا لەدەرەوەی پارەدانی سەرەتایی',
    'commercial.iqStore.feature4': 'بۆ فرۆشتنی قورسی بەرهەم و کۆگا',
    'commercial.iqStore.fit':
      'ئەگەر ئەولەویەتەکەت پارەدانی خێرای ڕۆژانەی کاونتەرە، لەوانەیە {glasspos} سادەتر بێت.',

    'commercial.raqib.tagline': 'Raqib — هەموو پابەندییە داراییەکانت لە یەک شوێن.',
    'commercial.raqib.description':
      'Raqib ئەپڵیکەیشنی چاودێری دارایی کاروبارە لە BeCoder. یارمەتیت دەدات جووڵەی پارە، داهات، خەرج و قەرز ببینیت — بۆ ئەوەی بزانیت چیت هەیە و چیت لەسەرە، و وێنەیەکی ڕوونتری ئەوەت هەبێت پارە بۆ کوێ دەڕوات. سیستەمی POS یان کاشێر نییە، سیستەمی کۆگا/کەتەلۆگ نییە، مێزی ئاڵوگۆڕ نییە، و ژمێریاری پشتڕاستکراو یان ERP قورس نییە. Raqib لە ئامادەکارییە بۆ کردنەوەی بۆ کڕیار لە BeCoder.',
    'commercial.raqib.whoLede':
      'بۆ خاوەن کاروبار و تیم کە پێویستیان بە چاودێری دارایی ڕوونتر هەیە — نەک کاونتەری فرۆشتنی تاکەفرۆشی.',
    'commercial.raqib.benefitsTitle': 'سوودە سەرەکییەکان',
    'commercial.raqib.explainHeading': 'Raqib بناسە',
    'commercial.raqib.explain.what.title': 'Raqib چییە؟',
    'commercial.raqib.explain.what.body':
      'Raqib چاودێری دارایی سادە و سەرپەرشتی پارەی کاروبارە. جووڵەی پارە ببینە، داهات و خەرج بە ڕوونی بمێنێتەوە، و لە قەرز و ئەوەی لەسەر کاروبارە ئاگادار بە — بەبێ ئاڵۆزیی سۆفتوێری ژمێریاری پشتڕاستکراو.',
    'commercial.raqib.explain.debts.title': 'قەرز و ئەوەی لەسەرتە',
    'commercial.raqib.explain.debts.body':
      'بەشێک لە مەبەستی Raqib بینینی ڕوونتری پارەی قەرزی کاروبار و ئەوەی کاروبار لەسەریەتی. ئامانج تێڕوانینێکی کردارییە بۆ ئەوەی چیت هەیە و چیت لەسەرە — نەک دەفتەری ژمێریاری تەواو یان کەشفی پشتڕاستکراو.',
    'commercial.raqib.explain.business.title': 'داهات و خەرجی کاروبار',
    'commercial.raqib.explain.business.body':
      'Raqib بۆ یارمەتیدانی خاوەنە بۆ تێگەیشتن لە جووڵەی پارەی کاروبار: داهاتی وەرگیراو، خەرجی کارکردن، و بینینێکی سادەتری دۆخی دارایی. گونجاوە بۆ کۆمپانیا، بەڵێندەر، ئۆفیس، بازرگان، دابەشکەر، وۆرکشۆپ، کاروباری بچووک و ناوەند، و خاوەن کاروبار.',
    'commercial.raqib.explain.not.title': 'Raqib چی نییە',
    'commercial.raqib.explain.not.body':
      'Raqib POS یان کاشێر نییە، سۆفتوێری بەرهەم/کۆگای فرۆشتن نییە، سیستەمی کارەکانی ئاڵوگۆڕ نییە، و ژمێریاری پشتڕاستکراو، باج، وردبینی یان ERP نییە.',
    'commercial.raqib.explain.diff.title': 'جیاوازی لەگەڵ بەرهەمەکانی دیکەی BeCoder',
    'commercial.raqib.explain.diff.body':
      'GlassPOS بۆ پارەدانی خێرای فرۆشتنی ڕۆژانە. IQ Store بۆ فرۆشتنی قورسی بەرهەم و کۆگا. BeFX بۆ ئۆفیسی ئاڵوگۆڕ. Raqib بۆ چاودێری پارەی کاروبار، قەرز، داهات و خەرج.',
    'commercial.raqib.problem1': 'جووڵەی پارە قورسە ببینرێت کاتێک تۆمارەکان لە دەفتەر و چات و فایل بڵاوبوونەتەوە.',
    'commercial.raqib.problem2': 'ئاسانە لەبیر بچێت کێ قەرزی کاروبارە و کاروبار چی لەسەرە.',
    'commercial.raqib.problem3': 'خەرج و داهاتی کاروبار زۆرجار ناڕوونە بەبێ کردنەوەی سیستەمی ژمێریاری قورس.',
    'commercial.raqib.problem4': 'خاوەن پێویستی بە وێنەیەکی سادەی دۆخی دارایی هەیە — نەک ئاڵۆزیی ژمێریاری پشتڕاستکراو.',
    'commercial.raqib.problem5': 'ئامێری POS و کۆگا جێگەی چاودێری دارایی ئارامی خودی کاروبار ناگرنەوە.',
    'commercial.raqib.feature1': 'چاودێری پارەی هاتوو و ڕۆیشتوو',
    'commercial.raqib.feature2': 'شوێنکەوتنی داهات، خەرج و قەرز',
    'commercial.raqib.feature3': 'بینینی ڕوونتری ئەوەی کاروبار هەیەتی و لەسەریەتی',
    'commercial.raqib.feature4': 'پوختەی دارایی سادە بۆ خاوەن و تیم',
    'commercial.raqib.feature5': 'چاودێری دارایی بەبێ پارەدانی POS',
    'commercial.raqib.feature6': 'سەرپەرشتی کرداری — نەک ژمێریاری پشتڕاستکراو یان ERP',
    'commercial.raqib.fit':
      'پێویستت بە پارەدانی خێرای فرۆشتن هەیە؟ {glasspos} یان {iq-store} هەڵبژێرە. پێویستت بە کارەکانی ئاڵوگۆڕ هەیە؟ {befx} هەڵبژێرە. Raqib بۆ چاودێری دارایی کاروبارە — پارە، قەرز، داهات و خەرج.',

    'commercial.befx.tagline': 'بەڕێوەبردنی تایبەت بۆ کارەکانی ئاڵوگۆڕ، دراو و قاسەکان.',
    'commercial.befx.description':
      'BeFX سۆفتوێری تایبەتە بۆ ئۆفیسی ئاڵوگۆڕ و کاروباری گۆڕینی دراو. سەرنج دەخاتە سەر کڕین و فرۆشتنی دراو، ڕێژەی ئاڵوگۆڕ، و جووڵەی قاسە و پارە بۆ کارەکانی ئۆفیسی ئاڵوگۆڕ — نەک چاودێری دارایی گشتی کاروبار.',
    'commercial.befx.problem1': 'کڕین و فرۆشتنی دراو بە کۆنترۆڵی ڕوونی ڕێژە ئەنجام بدە.',
    'commercial.befx.problem2': 'جووڵەی قاسە و پارە بۆ مێزی ئاڵوگۆڕ شوێنکەو.',
    'commercial.befx.problem3': 'کارە تایبەتەکانی ئاڵوگۆڕ ڕۆژانە ڕێکبخە.',
    'commercial.befx.feature1': 'کڕین و فرۆشتنی دراو بۆ ئۆفیسی ئاڵوگۆڕ',
    'commercial.befx.feature2': 'بەڕێوەبردنی ڕێژەی ئاڵوگۆڕ بۆ کارەکان',
    'commercial.befx.feature3': 'جووڵەی قاسە و پارە بۆ ڕێڕەوی ئاڵوگۆڕ',
    'commercial.befx.feature4': 'کارە تایبەتەکان — نەک سۆفتوێری دارایی گشتی',
    'commercial.befx.fit':
      'ئەگەر کاروبارەکەت ئاڵوگۆڕی دراو نییە و پێویستت بە چاودێری دارایی کاروبار هەیە، {raqib} لەبەرچاو بگرە.',
    'commercial.befx.thirdPartyRates':
      'کاتێک خزمەتگوزاریی داتای بازاڕ یان نامە بەکاردەهێنرێت، لەوانەیە تێچووی دابینکەر هەبێت.',

    'commercial.bepharma.tagline': 'بەڕێوەبردنی دەرمانخانە لەگەڵ چاودێری وردی کۆگا.',
    'commercial.bepharma.description':
      'BePharma سۆفتوێری دەرمانخانەی BeCoderە — دابەشکردن، کۆگا و تۆمار بەبێ ژاوەژاو.',
    'commercial.bepharma.problem1': 'کۆگای دەرمانخانە ورد بهێڵەوە بەبێ خاوکردنەوەی کاونتەر.',
    'commercial.bepharma.problem2': 'دابەشکردن و تۆمار لە یەک شوێن بەدواداچوون بکە.',
    'commercial.bepharma.problem3': 'کارەکانی دەرمانخانە بەڕێوەببە بەبێ ئەوەی وەک POSی فرۆشگای گشتی مامەڵەی لەگەڵ بکرێت.',
    'commercial.bepharma.feature1': 'کۆنترۆڵی کۆگای دەرمانخانە',
    'commercial.bepharma.feature2': 'دابەشکردن و تۆمار',
    'commercial.bepharma.feature3': 'کارەکانی ڕۆژانەی دەرمانخانە',
    'commercial.bepharma.feature4': 'بۆ دەرمانخانە دروستکراوە — نەک وەشانی کاشێری گشتی',
    'commercial.bepharma.fit':
      'ئەگەر پێویستت بە پارەدانی گشتیی خێرای فرۆشتن هەیە، {glasspos} سادەتر دەبێت. BePharma بۆ کارەکانی دەرمانخانەیە.',

    'commercial.fitTitle': 'دڵنیا نیت ئەمە گونجاوترینیە؟',

    'audience.supermarket': 'سوپەرمارکێت',
    'audience.minimarket': 'مینی مارکێت',
    'audience.grocery': 'فرۆشگای خۆراک',
    'audience.convenience': 'فرۆشگای نزیک و خزمەتی خێرا',
    'audience.bakery': 'نانەواخانە',
    'audience.confectionery': 'فرۆشگای شیرینی و شیرینکراو',
    'audience.beverage': 'فرۆشگای خواردنەوە',
    'audience.fastMovingRetail': 'کاونتەری فرۆشتنی خێراجووڵە',
    'audience.clothing': 'فرۆشگای جلوبەرگ',
    'audience.shoes': 'فرۆشگای پێڵاو',
    'audience.perfume': 'فرۆشگای بۆن',
    'audience.cosmetics': 'فرۆشگای جوانکاری',
    'audience.mobileShop': 'فرۆشگای مۆبایل',
    'audience.mobileAccessories': 'ئامێری مۆبایل',
    'audience.electronics': 'فرۆشگای ئەلیکترۆنیات',
    'audience.household': 'کاڵای ماڵەوە',
    'audience.homeware': 'فرۆشگای کەلوپەلی ماڵ',
    'audience.bookstore': 'کتێب و نووسین',
    'audience.spareParts': 'فرۆشگای پارچەی یەدەگ',
    'audience.generalRetail': 'فرۆشتنی گشتی',
    'audience.furniture': 'فرۆشگای مۆبیلیات',
    'audience.appliances': 'فرۆشگای ئامێری ماڵەوە',
    'audience.wholesaler': 'فرۆشتنی کۆ',
    'audience.warehouse': 'کۆگا',
    'audience.distributor': 'دابەشکەر',
    'audience.largeCatalog': 'کەتەلۆگی گەورە یان هەمەجۆر',
    'audience.heavyInventory': 'فرۆشتنی قورسی کۆگا',
    'audience.trading': 'بازرگان',
    'audience.serviceCo': 'کۆمپانیای خزمەتگوزاری',
    'audience.contractor': 'بەڵێندەر',
    'audience.office': 'ئۆفیس',
    'audience.factory': 'کارگە',
    'audience.company': 'کۆمپانیا',
    'audience.workshop': 'وۆرکشۆپ',
    'audience.sme': 'کاروباری بچووک و ناوەند',
    'audience.businessOwner': 'خاوەن کاروبار',
    'audience.projectFinance': 'چاودێری دارایی پڕۆژە',
    'audience.exchangeOffice': 'ئۆفیسی ئاڵوگۆڕ',
    'audience.currencyExchange': 'کۆمپانیای ئاڵوگۆڕی دراو',
    'audience.multiCurrency': 'مێزی فرەدراو',
    'audience.exchangeBranches': 'ئاڵوگۆڕ لەگەڵ قاسە',
    'audience.exchangeVaultOps': 'کارەکانی قاسە و پارەی ئاڵوگۆڕ',

    'commercial.whoTitle': 'ئەمە بۆ کێیە؟',
    'commercial.whoLede': 'گونجاوە بۆ کاروباری وەک ئەمانە — کاتێک لەگەڵ کارکردنت دەگونجێت.',
    'commercial.explainTitle': 'ئەم بەرهەمە چۆن یارمەتیت دەدات',
    'commercial.problemsTitle': 'چ کێشەیەک چارەسەر دەکات؟',
    'commercial.pricingTitle': 'نرخ',
    'commercial.pricingLede':
      'نرخ بۆ یەک بازاڕ لە هەر کاتێکدا پیشان دەدرێت. عێراق بە دیناری عێراقی. نێودەوڵەتی بە دۆلار. گۆڕینی زمان بازاڕی نرخ دیاری ناکات. ئەمانە نرخی سەربەخۆن — گۆڕینی دراوی زیندوو نین.',
    'commercial.pricingMarket': 'بازاڕی نرخ',
    'commercial.market.iraq': 'عێراق (IQD)',
    'commercial.market.international': 'نێودەوڵەتی (USD)',
    'commercial.market.iraqTab': 'عێراق',
    'commercial.market.internationalTab': 'نێودەوڵەتی',
    'commercial.plan.cloud_annual': 'خزمەتگوزارییەکانی کڵاود',
    'commercial.cloudNote':
      'خزمەتگوزاری میوانداری ئارەزوومەندانە. ئەگەر خزمەتگوزارییەکانی کڵاود بەسەرچن، مۆڵەتی هەمیشەیی دەمێنێتەوە، سۆفتوێری ناوخۆیی کاردەکات، و داتاکەت ناسڕێتەوە.',
    'commercial.internationalCheckoutNote':
      'نرخی دۆلاری نێودەوڵەتی بۆ ڕوونی پیشان دەدرێت. پارەدانی ئۆنلاین بە دۆلار هێشتا بەردەست نییە. پەیوەندی بە فرۆشتن بکە.',
    'commercial.trialTitle': 'تاقیکردنەوەی خۆڕایی بۆ ٣ ڕۆژ',
    'commercial.trialBody':
      'بەرهەمەکە بۆ ماوەی ٣ ڕۆژ بە تەواوی تاقی بکەرەوە. پێویستت بە هیچ شێوازێکی پارەدان نییە. تاقیکردنەوەیەکی خۆڕایی بۆ هەر بەرهەمێک.',
    'commercial.trialComingSoon':
      'تاقیکردنەوەی خۆڕایی بەردەست دەبێت کاتێک بەرهەم بۆ تاقیکردنەوەی کڕیار دەکرێتەوە. نرخەکانی خوارەوە پیشانی دەدەن چی چاوەڕوان بکرێت.',
    'commercial.plan.monthly': 'مانگانە',
    'commercial.plan.annual': 'ساڵانە',
    'commercial.plan.lifetime': 'مۆڵەتی هەمیشەیی',
    'commercial.plan.saveApprox': 'نزیکەی 17٪ کەمتر لە 12× مانگانە',
    'commercial.plan.saveApproxDynamic': 'نزیکەی {pct}٪ کەمتر لە 12× مانگانە',
    'commercial.lifetimeBadge': '٣ مانگ پشتگیری پریمیەم لەخۆدەگرێت',
    'commercial.lifetimeNoMandatory': 'خزمەتگوزارییەکانی کڵاود ئارەزوومەندانەن — بۆ مۆڵەت پێویست نین',
    'commercial.lifetimeTitle': 'مۆڵەتی هەمیشەیی',
    'commercial.lifetimeLede': 'یەکجار پارە بدە. هەمیشە بەکاربهێنە.',
    'commercial.lifetimeBody':
      'پارەدانی مۆڵەت یەکجارە لەگەڵ مافی هەمیشەیی بۆ بەکارهێنانی بەرهەمە مۆڵەتکراوەکە. هیچ نوێکردنەوەی مانگانە یان ساڵانەی ناچاری نییە. خزمەتگوزارییەکانی کڵاود ئارەزوومەندانەن و تەنها بۆ هاوکاتکردن و پاشەکەوتی میوانداری و خزمەتگوزاری پشتبەستوو بە سێرڤەرن. نوێنەکردنەوەی کڵاود مۆڵەتی هەمیشەیی ناکوژێتەوە، بەکارهێنانی ناوخۆیی ڕەوا ناگرێت، و داتاکەت ناسڕێتەوە. سێ مانگ پشتگیری تەکنیکیی پریمیەم لەخۆدەگرێت. دوای ئەوە کڕینی پشتگیری ئارەزوومەندانەیە.',
    'commercial.supportTitle': 'پشتگیری و چاککردنەوە',
    'commercial.supportBugsTitle': 'چاککردنەوەی هەڵە',
    'commercial.supportBugsBody':
      'خۆڕایی. هەر هەڵە یان کەموکوڕی یان خراپ کارکردنێک کە سۆفتوێری ئێستامان دەیكات بەبێ پارەی زیادە چاکدەکرێتەوە. چاککردنەوەی هەڵە مۆدیوولی پارەدار نییە، و پێویستت بە ئابوونەی چالاکی کڵاود نییە بۆ چاککردنەوەی تایبەتمەندییەکی کڕدراو. ئەمە گەشەپێدانی تایبەت یان ڕاوێژکاری بێسنوور نییە.',
    'commercial.supportSecurityTitle': 'چاککردنەوەی ئاسایش',
    'commercial.supportSecurityBody':
      'خۆڕایی. کەموکوڕیی ئاسایشی پەیوەندیدار بە بەرهەمەکەمان بەبێ پێویستی پلانی پشتگیری پارەدار چاکدەکرێتەوە.',
    'commercial.supportTechTitle': 'پشتگیری تەکنیکی',
    'commercial.supportTechBody':
      'مانگانە و ساڵانە: لە ماوەی ئابوونەی چالاکدا لەخۆدەگیرێت. هەمیشەیی: یەکەم ٣ مانگ لەخۆدەگیرێت؛ دوای ئەوە لەوانەیە پشتگیری پارەدار ئارەزوومەندانە پێشکەش بکرێت. هێشتا نرخی پشتگیری دوای ٣ مانگ بڵاونەکراوەتەوە.',
    'commercial.supportCustomTitle': 'گەشەپێدانی تایبەت',
    'commercial.supportCustomBody':
      'هەمیشە جیا نرخ دەدرێت — تایبەتمەندی، ڕاپۆرت، یەکخستن، ڕێڕەو یان گۆڕانکاری تایبەت بە کڕیار. گەشەپێدانی تایبەت وەک چاککردنەوەی هەڵەی بەرهەمی BeCoder نییە.',
    'commercial.thirdPartyTitle': 'تێچووی لایەنی سێیەم و خزمەتگوزاری',
    'commercial.thirdPartyBody':
      'مۆڵەتی هەمیشەیی مانای ئەوە نییە BeCoder هەموو تێچووی دەرەکیی دووبارەبووەوە هەمیشە هەڵدەگرێت. کاتێک گونجاو بێت، تێچووی جیا لەوانەیە بۆ میوانداریی کڵاود، SMS، واتساپ/API، یەکخستنی پارەدار، کۆگای زیاتر یان ژێرخانی گەورە هەبێت — هەر یەکە بە تێچووی دابینکەری خۆی.',
    'commercial.faqTitle': 'پێش کڕین',
    'commercial.faqLede': 'وەڵامی ڕاستەوخۆ بەپێی کارکردنی BeCoder ئەمڕۆ.',
    'commercial.updatesTitle': 'نوێکردنەوەکان',
    'commercial.updatesBody':
      'نوێکردنەوەی بەرهەم و چاککردنەوەی هەڵە و ئاسایش بۆ ئابوونەی مانگانە و ساڵانەی چالاک بەردەوامە، و بۆ مۆڵەتی هەمیشەیی وەک پاراستنی ئاسایی بۆ کەموکوڕیی ڕاستەقینەی بەرهەم.',
    'commercial.ctaLede': 'کاتێک بەردەستە تاقیبکەرەوە، یان لەگەڵ فرۆشتن قسە بکە بۆ مۆڵەت.',

    'commercial.plan.monthly.f1': 'مۆڵەتی مانگانەی دووبارەبووەوە',
    'commercial.plan.monthly.f2': 'بەکارهێنانی تەواوی ناوەڕۆکی سەرەکی لەکاتی چالاکیدا',
    'commercial.plan.monthly.f3': 'نوێکردنەوە و چاککردنەوەی هەڵە و ئاسایش',
    'commercial.plan.monthly.f4': 'پشتگیری تەکنیکی لە ماوەی ئابوونەی چالاکدا',
    'commercial.plan.annual.f1': 'هەمان سوودی سەرەکی مانگانە',
    'commercial.plan.annual.f2': '١٢ مانگ بە یەک پارەدان — نزیکەی 17٪ پاشەکەوت',
    'commercial.plan.annual.f3': 'نوێکردنەوە و چاککردنەوەی هەڵە و ئاسایش',
    'commercial.plan.annual.f4': 'پشتگیری تەکنیکی لە ماوەی ئابوونەی چالاکدا',
    'commercial.plan.lifetime.f1': 'یەکجار پارە بدە. هەمیشە بەکاربهێنە.',
    'commercial.plan.lifetime.f2': 'مافی هەمیشەیی بۆ بەکارهێنانی بەرهەمی مۆڵەتکراو',
    'commercial.plan.lifetime.f3': 'هیچ نوێکردنەوەی مانگانە یان ساڵانەی ناچاری نییە',
    'commercial.plan.lifetime.f4': '٣ مانگ پشتگیری تەکنیکیی پریمیەم لەخۆدەگرێت',
    'commercial.plan.lifetime.f5': 'پشتگیری پارەدار دوای ٣ مانگ ئارەزوومەندانەیە — مۆڵەت دەمێنێتەوە',

    'plan.period.lifetime': 'هەمیشەیی',
    'plan.name.monthly': 'مانگانە',
    'plan.name.annual': 'ساڵانە',
    'plan.name.lifetime': 'مۆڵەتی هەمیشەیی',
    'plan.name.cloud_annual': 'خزمەتگوزارییەکانی کڵاود',
    'showcase.plansPlaceholder': 'نرخەکان بە دۆلار. ساڵانە نزیکەی 17٪ پاشەکەوت دەکات بەراورد بە ١٢ مانگی مانگانە.',

    'faq.what.q': 'ئەم سۆفتوێریە چی دەکات؟',
    'faq.what.a': 'پوختە و تایبەتمەندییەکانی ئەم پەڕەیە ببینە بۆ تواناکانی ئەمڕۆ.',
    'faq.suitable.q': 'ئایا بۆ کاروبارەکەم گونجاوە؟',
    'faq.suitable.a':
      'بەشی «ئەمە بۆ کێیە؟» و ڕێنمایی گونجانی کورت لەم پەڕەیە بپشکنە. ئەگەر جۆری کاروبارەکەت لیستکراوە و وەسفەکە لەگەڵ کارکردنت دەگونجێت، گونجاوی بەهێزە — پەیوەندی بە فرۆشتن بکە ئەگەر دڵنیانیت.',
    'faq.trial.q': 'دەتوانم پێش کڕین تاقیبکەمەوە؟',
    'faq.trial.a':
      'بەرهەمە کڕینەکان تاقیکردنەوەی خۆڕایی ٣ ڕۆژ پێشکەش دەکەن کاتێک تاقیکردنەوە لەسەر BeCoder بەردەستە.',
    'faq.afterTrial.q': 'دوای تاقیکردنەوەی خۆڕایی ٣ ڕۆژە چی ڕوودەدات؟',
    'faq.afterTrial.a':
      'بە هەڵبژاردنی مانگانە، ساڵانە یان هەمیشەیی لە ڕێگەی مۆڵەتی BeCoder بەردەوام بە. پەیوەندی بە فرۆشتن بکە بۆ یارمەتی.',
    'faq.monthlyVsAnnual.q': 'جیاوازی مانگانە و ساڵانە چییە؟',
    'faq.monthlyVsAnnual.a':
      'هەمان کارایی سەرەکی بەرهەم. ساڵانە داشکاندنی پارەدانە — نزیکەی 17٪ کەمتر لە پارەدانی مانگانە بۆ ١٢ مانگ.',
    'faq.lifetime.q': 'مۆڵەتی هەمیشەیی واتای چییە؟',
    'faq.lifetime.a':
      'یەکجار پارە بدە بۆ مافی هەمیشەیی بەکارهێنانی بەرهەمی مۆڵەتکراو. هیچ نوێکردنەوەی مانگانە/ساڵانەی ناچاری نییە. سێ مانگ پشتگیری پریمیەم لەخۆدەگرێت.',
    'faq.lifetimeMonthly.q': 'دوای کڕینی هەمیشەیی دەبێت مانگانە پارە بدەم؟',
    'faq.lifetimeMonthly.a':
      'هیچ نوێکردنەوەی مانگانەی ناچاری بۆ خودی مۆڵەتی هەمیشەیی نییە. پشتگیری پارەداری ئارەزوومەندانە دوای ٣ مانگ جێگەی مۆڵەت ناگرێتەوە.',
    'faq.afterSupport.q': 'دوای ٣ مانگی پشتگیری خۆڕایی چی ڕوودەدات؟',
    'faq.afterSupport.a':
      'مۆڵەتی هەمیشەیی دەمێنێتەوە. کڕینی پشتگیری تەکنیکی دوای ئەوە ئارەزوومەندانەیە؛ هێشتا ئەو نرخانە بڵاونەکراونەتەوە.',
    'faq.supportMandatory.q': 'ئایا پشتگیری پارەدار ناچارییە؟',
    'faq.supportMandatory.a':
      'نەخێر. ڕەتکردنەوەی پشتگیری پارەدار دوای ماوەی لەخۆگیراوی هەمیشەیی مۆڵەتەکە ناکوژێتەوە.',
    'faq.bugFixes.q': 'ئایا چاککردنەوەی هەڵەی ڕاستەقینە خۆڕاییە؟',
    'faq.bugFixes.a':
      'بەڵێ. ئەگەر کەموکوڕییەکە لە سۆفتوێرکەمانەوە بێت، تەنها بۆ چاککردنەوەکەی پارەت لێناگیرێت.',
    'faq.securityFixes.q': 'ئایا چاککردنەوەی ئاسایش خۆڕاییە؟',
    'faq.securityFixes.a':
      'بەڵێ. کەموکوڕیی ئاسایشی پەیوەندیدار بە بەرهەمەکەمان بەبێ پێویستی پلانی پشتگیری پارەدار چاکدەکرێتەوە.',
    'faq.customDev.q': 'چی بە گەشەپێدانی تایبەت دادەنرێت؟',
    'faq.customDev.a':
      'تایبەتمەندی، ڕاپۆرت، یەکخستن، ڕێڕەو یان گۆڕانکاری تایبەت بە کڕیار — هەمیشە جیا نرخ دەدرێت، و وەک چاککردنەوەی هەڵەی بەرهەم نییە.',
    'faq.thirdParty.q': 'ئایا خزمەتگوزاریی لایەنی سێیەم لەخۆدەگیرێت؟',
    'faq.thirdParty.a':
      'خۆکارانە نا. میوانداریی تایبەت، SMS، واتساپ/API، یەکخستنی پارەدار، کۆگای زیاتر یان ژێرخانی گەورە لەوانەیە تێچووی دابینکەری جیا هەبێت کاتێک بەکاردەهێنرێن.',

    'faq.raqibWhat.q': 'Raqib چییە؟',
    'faq.raqibWhat.a':
      'Raqib بەرهەمی چاودێری دارایی کاروبارە لە BeCoder — جووڵەی پارە، داهات، خەرج و قەرز. لە ئامادەکارییە بۆ کردنەوەی بۆ کڕیار (بەمزوانێک). POS نییە، سیستەمی کۆگا نییە، مێزی ئاڵوگۆڕ نییە، و ژمێریاری پشتڕاستکراو یان ERP نییە.',
    'faq.raqibCompaniesOnly.q': 'ئایا Raqib تەنها بۆ کۆمپانیایە؟',
    'faq.raqibCompaniesOnly.a':
      'Raqib بۆ بەکارهێنانی کاروبارە: کۆمپانیا، بەڵێندەر، ئۆفیس، بازرگان، دابەشکەر، وۆرکشۆپ، کاروباری بچووک و ناوەند، و خاوەن کاروبار کە پێویستیان بە چاودێری دارایی ڕوونتر هەیە.',
    'faq.raqibPersonal.q': 'دەتوانم Raqib بۆ خەرجی کەسی یان خێزانی بەکاربهێنم؟',
    'faq.raqibPersonal.a':
      'وەک توانای بڵاوکراوەی ئەمڕۆ نا. شوێنگرتنی ئێستا چاودێری دارایی کاروبارە. دۆخی دارایی کەسی و خێزانی لەم پەڕەیەدا وەک تایبەتمەندی بەردەست پێشکەش ناکرێت.',
    'faq.raqibIOwe.q': 'دەتوانم پارەی لەسەرم بۆ کەسێک تۆمار بکەم؟',
    'faq.raqibIOwe.a':
      'شوێنگرتنی بڵاوکراوەی Raqib بینینی ڕوونتری ئەوەی کاروبار لەسەریەتی لەخۆدەگرێت. دەفتەری قەرزی کەسی ورد و ڕێڕەوی قەست وەک تایبەتمەندی بەردەست هێشتا بانگەشەی بۆ ناکرێت.',
    'faq.raqibOwesMe.q': 'دەتوانم پارەی کەسێک لەسەری بۆ من تۆمار بکەم؟',
    'faq.raqibOwesMe.a':
      'قەرز — پارەی قەرزی کاروبار — بەشێکە لە شوێنگرتنی چاودێری دارایی Raqib. وردەکاریی تۆمارکردن لەگەڵ وەشانی کڕیار دەگونجێت کاتێک بەرهەم دەکرێتەوە.',
    'faq.raqibPartial.q': 'دەتوانم پارەدانی بەشەکی قەرز و باڵانسی ماوە شوێن بکەوم؟',
    'faq.raqibPartial.a':
      'وەک توانای بڵاوکراوەی ئەمڕۆ نا. شوێنکەوتنی پارەدانی بەشەکی، باڵانسی ماوە و مێژووی وەرگرتن لەم پەڕەیەدا وەک تایبەتمەندی بەردەست بانگەشەی بۆ ناکرێت.',
    'faq.raqibSubscriptions.q': 'دەتوانم بەشداری و پابەندیی دووبارەبوو شوێن بکەوم؟',
    'faq.raqibSubscriptions.a':
      'وەک توانای بڵاوکراوەی ئەمڕۆ نا. شوێنکەوتنی بەشداری و پسوڵەی دووبارەبوو (ئینتەرنێت، کرێ، قەست و هاوشێوە) لەم پەڕەیەدا وەک تایبەتمەندی بەردەست بانگەشەی بۆ ناکرێت.',
    'faq.raqibBusinessMoney.q': 'دەتوانم بۆ چاودێری داهات و خەرجی کاروبار بەکاریبهێنم؟',
    'faq.raqibBusinessMoney.a':
      'بەڵێ — ئەمە ناوەڕۆکی مەبەستی بڵاوکراوەی Raqibە: چاودێری جووڵەی پارەی کاروبار، داهات و خەرج بۆ بینینی ڕوونتری خاوەن، بەبێ ئاڵۆزیی ژمێریاری پشتڕاستکراو.',
    'faq.raqibIsPos.q': 'ئایا Raqib سیستەمی POSە؟',
    'faq.raqibIsPos.a':
      'نەخێر. بۆ پارەدانی خێرای فرۆشتنی ڕۆژانە GlassPOS ببینە. بۆ فرۆشتنی قورسی بەرهەم و کۆگا IQ Store ببینە.',
    'faq.raqibIsInventory.q': 'ئایا Raqib سیستەمی کۆگایە؟',
    'faq.raqibIsInventory.a':
      'نەخێر. ڕێکخستنی بەرهەم و کۆگا هی IQ Storeە (و ئامێری فرۆشتنی پەیوەندیدار)، نەک Raqib.',
    'faq.raqibIsErp.q': 'ئایا Raqib سیستەمی ژمێریاری یان ERPی تەواوە؟',
    'faq.raqibIsErp.a':
      'نەخێر. Raqib چاودێری دارایی و سەرپەرشتی پارەی کاروبارە — نەک ژمێریاری پشتڕاستکراو، باج، وردبینی یان ERP.',
    'faq.raqibVsOthers.q': 'جیاوازی Raqib و GlassPOS و IQ Store چییە؟',
    'faq.raqibVsOthers.a':
      'GlassPOS: POSی فرۆشتنی خێراجووڵە. IQ Store: فرۆشتنی قورسی بەرهەم/کۆگا. Raqib: چاودێری دارایی کاروبار — پارە، قەرز، داهات و خەرج. BeFX بۆ کارەکانی ئاڵوگۆڕە.',
  },
};
